<template>
  <div>
    <!-- Render section if item has sectionName and subQuestions -->
    <div v-if="isSection(item)" class="section-container mb-6 border border-gray-300 rounded-lg">
      <div class="section-header bg-gray-300 p-3 rounded-t-lg font-medium border-b border-gray-600">
        {{ item.sectionName }}
      </div>
      <div class="section-content p-4">
        <!-- Recursively render subQuestions using a dynamic component approach -->
        <div v-for="(subItem, index) in item.subQuestions" :key="getUniqueKey(subItem, index)">
          <QuestionPreview
            :item="subItem"
            :get-type-color="getTypeColor"
            :get-type-icon="getTypeIcon"
            :banner-logo-url="bannerLogoUrl"
            :edit-question="() => editQuestion(index)"
            :delete-question="() => deleteQuestion(index)"
            class="mb-4"
          />
        </div>
      </div>
    </div>

    <!-- Render regular question -->
    <v-card v-else class="question-card mb-4 rounded-lg shadow-sm" outlined>
      <v-card-text class="p-4">
        <!-- Question Header -->
        <div class="flex justify-between items-start mb-3">
          <div class="flex items-center p-2">
            <v-icon :color="getTypeColor(item.questionType)" class="mr-2">
              {{ getTypeIcon(item.questionType) }}
            </v-icon>
            <h3 class="font-medium text-md mb-0">
              {{ item.question }}
              <span v-if="item.isRequired" class="text-red-500">*</span>
            </h3>
          </div>
          <div class="flex justify-end p-2">
            <v-btn icon small @click="editQuestion" v-tooltip="{ text: 'Edit' }">
              <v-icon small color="blue">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small @click="deleteQuestion" v-tooltip="{ text: 'Delete' }">
              <v-icon small color="red">mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Question Preview -->
        <div class="preview-container bg-gray-50 p-4 rounded-lg">
          <!-- Text Input Preview -->
          <v-text-field
            v-if="item.questionType === 'TEXT'"
            label="Your answer"
            outlined
            dense
            disabled
            hide-details
            placeholder="Text answer"
          ></v-text-field>

          <!-- Textarea Preview -->
          <v-textarea
            v-if="item.questionType === 'TEXTAREA'"
            label="Your answer"
            outlined
            dense
            disabled
            hide-details
            placeholder="Longer text answer"
            rows="2"
          ></v-textarea>

          <!-- Number Input Preview -->
          <v-text-field
            v-if="item.questionType === 'NUMBER'"
            label="Your answer"
            outlined
            dense
            disabled
            type="number"
            placeholder="0"
            hide-details
          ></v-text-field>

          <!-- Checkbox Preview -->
          <div v-if="item.questionType === 'CHECKBOX'">
            <v-checkbox
              v-for="(option, i) in item.options"
              :key="i"
              :label="option"
              disabled
              dense
              hide-details
              class="mt-0 mb-1"
            ></v-checkbox>
          </div>

          <!-- Radio Preview -->
          <div v-if="item.questionType === 'RADIO'">
            <v-radio-group disabled>
              <v-radio
                v-for="(option, i) in item.options"
                :key="i"
                :label="option"
                :value="option"
                dense
                hide-details
                class="mt-0 mb-1"
              ></v-radio>
            </v-radio-group>
          </div>

          <!-- File Upload Preview -->
          <div
            v-if="item.questionType === 'LOCAL_ATTACHMENTS'"
            class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center"
          >
            <v-icon size="32" color="grey lighten-1">mdi-cloud-upload</v-icon>
            <p class="text-sm text-gray-500">Drag & drop files here or click to browse</p>
            <p class="text-xs text-gray-400 mt-1">
              Maximum {{ item.allowedNoOfAttachments || 1 }} file(s)
            </p>
          </div>

          <!-- Media Capture Previews -->
          <div
            v-if="item.questionType === 'CLICK_PICTURES'"
            class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center"
          >
            <v-icon size="32" color="grey lighten-1">mdi-camera</v-icon>
            <p class="text-sm text-gray-500">Upload image or take a photo</p>
          </div>

          <div
            v-if="item.questionType === 'AUDIO_CAPTURE'"
            class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center"
          >
            <v-icon size="32" color="grey lighten-1">mdi-microphone</v-icon>
            <p class="text-sm text-gray-500">Record audio or upload file</p>
          </div>

          <div
            v-if="item.questionType === 'VIDEO_CAPTURE'"
            class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center"
          >
            <v-icon size="32" color="grey lighten-1">mdi-video</v-icon>
            <p class="text-sm text-gray-500">Record video or upload file</p>
          </div>

          <!-- SERVICE_PLAN preview -->
          <div v-if="item.questionType === 'SERVICE_PLAN'" class="rounded-lg">
            <div v-if="item.payment_Plan && item.payment_Plan.length > 0">
              <h4 class="font-medium mb-2 pl-1">Available Plans</h4>
              <div
                v-for="(plan, planIndex) in item.payment_Plan"
                :key="planIndex"
                class="mb-3 p-3 border rounded-lg bg-white"
              >
                <div class="font-medium">{{ plan.planName }}</div>
                <div class="text-sm text-gray-600">
                  {{ plan.planDescription }}
                </div>
                <div class="flex justify-between mt-2">
                  <span class="text-sm">Duration: {{ plan.planDuration }} days</span>
                  <span class="font-medium">Amount: {{ plan.planAmount }}</span>
                </div>
              </div>
              <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <div v-if="item.options && item.options.length > 0">
                  <img
                    :src="bannerLogoUrl(item.options[0])"
                    class="mx-auto h-40 object-contain mb-2"
                    alt="Payment QR"
                  />
                  <p class="text-sm text-gray-500">Scan QR code to make payment</p>
                </div>
                <div v-else>
                  <v-icon size="32" color="grey lighten-1">mdi-qrcode</v-icon>
                  <p class="text-sm text-gray-500">Payment QR code will appear here</p>
                </div>
              </div>
            </div>
            <div v-else>
              <v-icon size="32" color="grey lighten-1">mdi-currency-usd</v-icon>
              <p class="text-sm text-gray-500">Payment plans will appear here</p>
            </div>
          </div>

          <!-- DATA_CATEGORY preview -->
          <div v-if="item.questionType === 'DATA_CATEGORY'" class="mt-2">
            <v-select
              disabled
              :items="item.options || []"
              label="Select data category"
              outlined
              dense
              hide-details
            ></v-select>
          </div>
          <!-- SELECT_OPTIONS preview -->
          <div v-if="item.questionType === 'SELECT_OPTIONS'" class="mt-2">
            <v-select
              disabled
              :items="item.options || []"
              label="Select options"
              outlined
              dense
              hide-details
            ></v-select>
          </div>

          <!-- DATE preview -->
          <div v-if="item.questionType === 'DATE'" class="mt-2">
            <v-text-field
              disabled
              label="Select Date"
              prepend-icon="mdi-calendar"
              outlined
              dense
              hide-details
              placeholder="YYYY-MM-DD"
            ></v-text-field>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'QuestionPreview',
  props: {
    item: {
      type: Object,
      required: true
    },
    getTypeColor: {
      type: Function,
      required: true
    },
    getTypeIcon: {
      type: Function,
      required: true
    },
    bannerLogoUrl: {
      type: Function,
      required: true
    },
    editQuestion: {
      type: Function,
      required: true
    },
    deleteQuestion: {
      type: Function,
      required: true
    }
  },
  computed: {
    isSection() {
      return (item) => item.sectionName && Array.isArray(item.subQuestions)
    }
  },
  methods: {
    getUniqueKey(item, index) {
      // Generate a unique key for v-for
      if (item._id) {
        return item._id
      } else if (item.question) {
        return `${item.question}-${index}`
      } else if (item.sectionName) {
        return `${item.sectionName}-${index}`
      }
      return index
    }
  }
}
</script>

<style scoped>
.question-card {
  transition: all 0.3s ease;
}

.question-card:hover {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.preview-container {
  transition: all 0.3s ease;
}

.preview-container:hover {
  border-color: #3b82f6;
}

.section-container {
  transition: all 0.3s ease;
}

.section-container:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}
</style>
